import React from 'react';
import { X } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface RailroadProperties {
  id?: string;
  name?: string | null;
  owner?: string | null;
  network_type?: string | null;
}

interface RailroadDetailProps {
  railroad: RailroadProperties;
  onClose: () => void;
}

// Owner-code → human name + profile. Covered Class I carriers; unknown codes fall through
// to a generic profile keyed off `network_type` so the panel still renders usefully.
const OWNER_PROFILES: Record<string, { fullName: string; classification: string; about: string; btmNote: string }> = {
  CSXT: {
    fullName: 'CSX Transportation',
    classification: 'Class I Freight Railroad',
    about:
      'CSX operates one of the largest Class I freight networks in the Eastern US (~19,500 route miles across 23 states). Traffic mix includes coal, intermodal, chemicals, and merchandise. Interchanges with NS, CN, and regional short-lines.',
    btmNote:
      'For BTM campuses, rail proximity matters for oversized-turbine delivery (7–9 MW Siemens SGT-800, GE LM6000) and structural-steel laydown during construction. CSX serves the Appalachian coal corridor including Harrison County WV; most sites within 5 mi of active track can negotiate a short-term construction siding. CSX freight marketing (customers.csx.com) handles new-customer routing and facility studies.',
  },
  BNSF: {
    fullName: 'BNSF Railway',
    classification: 'Class I Freight Railroad',
    about:
      'BNSF is the largest freight railroad in North America (~32,500 route miles, 28 states). Berkshire Hathaway subsidiary. Strong in the western US, Permian Basin, and Pacific Northwest.',
    btmNote:
      'BNSF serves the Permian heavily and is often the nearest Class I carrier for Pecos/Reeves/Ward County sites. Active intermodal terminals at Alliance (Fort Worth) and Barstow route turbine-class loads. BNSF Economic Development (bnsf.com/ship-with-bnsf) works directly with new industrial customers.',
  },
  UP: {
    fullName: 'Union Pacific Railroad',
    classification: 'Class I Freight Railroad',
    about:
      'UP operates ~32,200 route miles across 23 western states. Largest intermodal network in the US; heavy chemical, automotive, and agricultural traffic. Major Permian exposure.',
    btmNote:
      'UP is the dominant Class I across West Texas, with main lines through Midland, Odessa, and Pecos. UP Industrial Development (up.com/aboutup/economic_development/) publishes a site-readiness program for sites with access to an active spur.',
  },
  NS: {
    fullName: 'Norfolk Southern Railway',
    classification: 'Class I Freight Railroad',
    about:
      'NS operates ~19,300 route miles across 22 eastern states. Traditional strengths in coal, intermodal, and automotive. Major overlap with CSX in Appalachia and the Southeast.',
    btmNote:
      'NS provides alternative Class I access in eastern US markets. In WV/PA, NS and CSX often parallel each other — having both within 5 mi can be used as leverage in freight-rate negotiations.',
  },
  CN: {
    fullName: 'Canadian National Railway',
    classification: 'Class I Freight Railroad',
    about:
      'CN runs ~19,500 route miles between Canada and the US Gulf Coast via Chicago and Memphis. Primary access to Gulf Coast ports and Mexico.',
    btmNote:
      'CN rarely the primary carrier for Permian or Appalachian BTM sites, but matters for Gulf Coast or Midwest siting.',
  },
  CP: {
    fullName: 'Canadian Pacific Railway',
    classification: 'Class I Freight Railroad',
    about:
      'CP operates ~13,000 route miles across Canada and the US upper Midwest. Recently merged with KCS (CPKC) creating a US–Mexico through-line.',
    btmNote:
      'CPKC is most relevant for Midwest and Texas-to-Mexico routing. Limited overlap with core BTM markets.',
  },
  AO: {
    fullName: 'Appalachian & Ohio Railroad',
    classification: 'Short-line / Regional',
    about:
      'Regional short-line serving West Virginia, typically connecting to CSX or NS at interchange points. Short-lines often handle local industrial switching at lower per-car rates than Class I.',
    btmNote:
      'Short-lines matter for the last-mile switching quote. For WV sites, A&O interchanges with CSX give a negotiation path when CSX Class I rates are uncompetitive.',
  },
  WVC: {
    fullName: 'West Virginia Central Railroad',
    classification: 'Short-line / Regional',
    about:
      'Regional short-line operating in central West Virginia. Handles coal, aggregates, and general merchandise; typically interchanges with CSX.',
    btmNote:
      'Useful as a secondary carrier option for Central WV BTM sites.',
  },
};

const GENERIC_PROFILE = {
  fullName: null as string | null,
  classification: 'Freight Railroad',
  about:
    'Freight rail corridor. Owner-code and network classification come from the Federal Railroad Administration (FRA) National Rail Network dataset. Not all owner codes in the FRA dataset map to a well-known Class I carrier — some represent short-lines, terminal railroads, or trackage-rights operators.',
  btmNote:
    'Rail proximity matters for BTM construction logistics (turbine delivery, structural steel). For unfamiliar owner codes, contact the FRA or the operator directly to verify trackage ownership and active service.',
};

export const RailroadDetail: React.FC<RailroadDetailProps> = ({ railroad, onClose }) => {
  const { theme } = useTheme();
  const ownerCode = (railroad.owner || railroad.name || '').toUpperCase();
  const profile = OWNER_PROFILES[ownerCode] || GENERIC_PROFILE;
  const displayName = profile.fullName || ownerCode || 'Railroad';

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <span style={{
              width: '14px', height: '4px', borderRadius: '1px',
              background: '#78716c', flexShrink: 0,
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.3)',
            }} />
            <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>{displayName}</h2>
          </div>
          <div style={{ fontSize: '10px', color: theme.textSecondary, marginLeft: '22px' }}>
            {profile.classification} · {ownerCode || 'Railroad'}
          </div>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        <Section title="Segment Attributes">
          <Field label="Owner Code" value={ownerCode || '—'} />
          {railroad.name && railroad.name !== railroad.owner && <Field label="Line Name" value={railroad.name} />}
          {railroad.network_type && <Field label="Network Type" value={railroad.network_type} />}
        </Section>

        <Section title="About">
          <div style={{ fontSize: '11px', color: theme.text, lineHeight: 1.55 }}>
            {profile.about}
          </div>
        </Section>

        <Section title="BTM Siting Context">
          <div style={{
            padding: '8px 10px', marginBottom: '6px', borderRadius: '6px',
            background: 'rgba(120,113,108,0.08)', border: '1px solid rgba(120,113,108,0.25)',
          }}>
            <div style={{ fontSize: '11px', color: theme.text, lineHeight: 1.55 }}>
              {profile.btmNote}
            </div>
          </div>
        </Section>

        <Section title="Data Source">
          <div style={{ fontSize: '10px', color: theme.textSecondary, lineHeight: 1.5 }}>
            FRA National Rail Network (public dataset). Geometry and owner codes are FRA-curated; our load preserves them as-is. Owner codes do not always resolve to brand names — the FRA uses historic RRUID codes that may predate mergers.
          </div>
        </Section>
      </div>
    </div>
  );
};
